// Multi-select chip strip for the lifecycle stages. The selected set lives
// in the filter-members Zustand store; this component is a controlled view
// over `selectedStages` / `toggleStage`.
//
// Each chip's "selected" affordance is both color-coded (to match the row
// badges from entities/member) AND announces aria-pressed for screen readers.

import {
  MEMBER_LIFECYCLE_STAGES,
  type MemberLifecycleStage,
  type MemberStageCounts,
} from "@community-analytics/shared";

import { stageStyle } from "@entities/member";
import { cn } from "@shared/lib";

import {
  selectSelectedStages,
  useFilterMembersStore,
} from "../model/filter-store";

export interface StageFilterChipsProps {
  /** Per-stage counts from the API. Always rendered alongside the label. */
  counts: MemberStageCounts;
  /** Externalized labels — caller maps stage → display string. */
  labelsByStage: Readonly<Record<MemberLifecycleStage, string>>;
  /** Accessible group label, e.g. "Filter by lifecycle stage". */
  ariaLabel: string;
  className?: string;
}

export function StageFilterChips({
  counts,
  labelsByStage,
  ariaLabel,
  className,
}: StageFilterChipsProps) {
  const selectedStages = useFilterMembersStore(selectSelectedStages);
  const toggleStage = useFilterMembersStore((s) => s.toggleStage);
  const selected = new Set(selectedStages);

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {MEMBER_LIFECYCLE_STAGES.map((stage) => {
        const isOn = selected.has(stage);
        const style = stageStyle(stage);
        return (
          <button
            key={stage}
            type="button"
            aria-pressed={isOn}
            onClick={() => toggleStage(stage)}
            className={cn(
              "inline-flex h-9 min-w-[44px] items-center gap-1 rounded-full border px-3 text-xs font-medium",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "transition-colors",
              isOn
                ? cn(style.backgroundClass, style.textClass, style.borderClass)
                : "border-input bg-background text-muted-foreground hover:bg-muted",
            )}
            data-stage={stage}
          >
            <span className="uppercase tracking-wide">
              {labelsByStage[stage]}
            </span>
            <span className="tabular-nums opacity-80">{counts[stage]}</span>
          </button>
        );
      })}
    </div>
  );
}
